import React, { useEffect, useState } from 'react';
import Alert from 'antd/lib/alert';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import Switch from 'antd/lib/switch';

import { t } from 'utils/i18n';
import {
    fetchLibraries, fetchProjectStepDefaults, LibraryOption, ProjectStepDefaults, saveProjectStepDefaults,
    StepSettings,
} from './steps-api';

interface Props {
    projectId: number;
    onClose(saved: boolean): void;
}

function messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
}

export default function StepDefaultsModal({ projectId, onClose }: Props): JSX.Element {
    const [defaults, setDefaults] = useState<ProjectStepDefaults | null>(null);
    const [libraries, setLibraries] = useState<LibraryOption[]>([]);
    const [draft, setDraft] = useState<StepSettings>({});
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Promise.all([fetchProjectStepDefaults(projectId), fetchLibraries().catch(() => [] as LibraryOption[])])
            .then(([loaded, options]) => {
                if (cancelled) return;
                setDefaults(loaded);
                setDraft(loaded.settings);
                setLibraries(options);
            })
            .catch((e) => {
                if (!cancelled) setError(messageOf(e));
            });
        return () => { cancelled = true; };
    }, [projectId]);

    const save = async (): Promise<void> => {
        setSaving(true);
        try {
            await saveProjectStepDefaults(projectId, draft);
            onClose(true);
        } catch (e) {
            setError(messageOf(e));
            setSaving(false);
        }
    };

    return (
        <Modal
            visible
            destroyOnClose
            title={t('Models and defaults')}
            okText={t('Save')}
            onOk={save}
            onCancel={() => onClose(false)}
            confirmLoading={saving}
            okButtonProps={{ disabled: !defaults }}
            className='cvat-step-defaults-modal'
        >
            {error && <Alert type='error' showIcon message={error} />}
            {!defaults && !error && <Spin />}
            {defaults && (
                <div className='cvat-step-defaults'>
                    <p className='cvat-step-defaults-hint'>
                        {t('Tasks of this project use these, unless a task picks its own model.')}
                    </p>
                    {defaults.steps.filter((step) => step.kind !== 'align').map((step) => {
                        const enabled = draft.enabled?.[step.id] ?? step.enabled_by_default;
                        const chosen = draft.functions?.[step.id] ?? step.candidates[0]?.id;
                        return (
                            <div key={step.id} className='cvat-step-defaults-row'>
                                <Switch
                                    size='small'
                                    checked={!step.unavailable_reason && !!enabled}
                                    disabled={!!step.unavailable_reason}
                                    aria-label={t('{title} on', { title: t(step.title) })}
                                    onChange={(on) => setDraft((prev) => ({
                                        ...prev, enabled: { ...(prev.enabled || {}), [step.id]: on },
                                    }))}
                                />
                                <span className='cvat-step-defaults-title'>{t(step.title)}</span>
                                {step.kind === 'lambda' && (
                                    <Select
                                        size='small'
                                        virtual={false}
                                        className='cvat-step-defaults-model'
                                        aria-label={t('{title} model', { title: t(step.title) })}
                                        value={chosen ?? undefined}
                                        onChange={(value: string) => setDraft((prev) => ({
                                            ...prev, functions: { ...(prev.functions || {}), [step.id]: value },
                                        }))}
                                    >
                                        {step.candidates.map((candidate) => (
                                            <Select.Option
                                                key={candidate.id}
                                                value={candidate.id}
                                                disabled={candidate.deployed === false}
                                            >
                                                {`${candidate.name}${candidate.recommended ? ` · ${t('recommended')}` : ''}`}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                                {step.unavailable_reason && (
                                    <span className='cvat-step-defaults-reason'>{t(step.unavailable_reason)}</span>
                                )}
                            </div>
                        );
                    })}
                    <div className='cvat-step-defaults-row'>
                        <span className='cvat-step-defaults-title'>{t('Libraries for Find furniture in library')}</span>
                        <Select
                            mode='multiple'
                            size='small'
                            virtual={false}
                            className='cvat-step-defaults-libraries'
                            aria-label={t('Libraries')}
                            placeholder={t('Used when the task has no references')}
                            value={draft.libraries ?? []}
                            onChange={(value: string[]) => setDraft((prev) => ({ ...prev, libraries: value }))}
                        >
                            {libraries.map((library) => (
                                <Select.Option key={library.key} value={library.value}>{library.key}</Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>
            )}
        </Modal>
    );
}
